import { useCallback, useEffect, useRef, useState } from "react"

import type { TakeawayProductGroup } from "./takeawayTypes"

// Combined height of sticky date bar + category bar in px (used for scroll offset)
const STICKY_HEADER_HEIGHT = 140

type UseTakeawayScrollGroupsResult = Readonly<{
    rootRef: React.RefObject<HTMLDivElement>
    activeGroupId: number | null
    setActiveGroupId: (id: number | null) => void
    scrollToGroup: (groupId: number) => void
}>

// How long (ms) to suppress the IntersectionObserver after a manual category click
const SCROLL_LOCK_MS = 600

export function useTakeawayScrollGroups(
    groups: TakeawayProductGroup[],
    showFavorites: boolean,
): UseTakeawayScrollGroupsResult {
    const rootRef = useRef<HTMLDivElement>(null)
    const [activeGroupId, setActiveGroupId] = useState<number | null>(null)
    const scrollLockRef = useRef(false)
    const scrollLockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        if (showFavorites || activeGroupId !== null) return
        const first = groups[0]
        if (first) setActiveGroupId(first.id)
    }, [groups, showFavorites, activeGroupId])

    // Scroll-driven tab highlighting via IntersectionObserver
    useEffect(() => {
        const container = rootRef.current
        if (!container || showFavorites) return

        const observer = new IntersectionObserver(
            (entries) => {
                // Suppress observer updates while user-initiated scroll is in progress
                if (scrollLockRef.current) return

                // Collect all currently intersecting group IDs, pick the topmost
                const intersecting = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)

                if (intersecting.length > 0) {
                    const id = Number(intersecting[0].target.id.replace("group-", ""))
                    if (!isNaN(id)) setActiveGroupId(id)
                }
            },
            {
                root: container,
                // Shrink effective viewport: exclude sticky header from top, keep only upper half
                rootMargin: `-${STICKY_HEADER_HEIGHT}px 0px -50% 0px`,
                threshold: 0,
            },
        )

        groups.forEach((group) => {
            const el = document.getElementById(`group-${group.id}`)
            if (el) observer.observe(el)
        })

        return () => observer.disconnect()
    }, [groups, showFavorites])

    const scrollToGroup = useCallback((groupId: number) => {
        const el = document.getElementById(`group-${groupId}`)
        const container = rootRef.current
        if (!el || !container) return

        // Lock the observer so the click-selected group stays active during scroll
        scrollLockRef.current = true
        if (scrollLockTimerRef.current) clearTimeout(scrollLockTimerRef.current)
        scrollLockTimerRef.current = setTimeout(() => {
            scrollLockRef.current = false
        }, SCROLL_LOCK_MS)

        const containerRect = container.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        container.scrollTo({
            top: container.scrollTop + elRect.top - containerRect.top - STICKY_HEADER_HEIGHT,
            behavior: "smooth",
        })
    }, [])

    return { rootRef, activeGroupId, setActiveGroupId, scrollToGroup }
}
